import type { TextStyleCategory, TextStyleColor, TextStyleSize, TextStyleVariant } from './text-style.types';

/**
 * Builds the typography utility class for a text style.
 *
 * This is the dynamic counterpart to writing a static `class="sir-text-style-…"`:
 * use it when the category/size/variant are bound at runtime.
 *
 * The returned class is styled by the global typography stylesheet, which a host
 * app loads once via `@use '@sirius/typography';` in its global stylesheet. No
 * Angular runtime is involved — this is a pure string builder.
 *
 * @example
 * ```ts
 * // static (prefer plain class / host class):
 * //   <p class="sir-text-style-paragraph-m-default">…</p>
 * //   host: { class: 'sir-text-style-paragraph-m-default' }
 *
 * // dynamic:
 * [class]="textStyleClass('title', size())"
 * ```
 */
export function textStyleClass(
    category: TextStyleCategory,
    size: TextStyleSize = 'm',
    variant: TextStyleVariant = 'default'
): string {
    return `sir-text-style-${category}-${size}-${variant}`;
}

/**
 * Builds the color override utility class for a text style.
 *
 * Combine with {@link textStyleClass} when a category's semantic color must be
 * overridden (e.g. `[class]="textStyleClass('paragraph', 'm') + ' ' + textStyleColorClass('secondary')"`).
 */
export function textStyleColorClass(color: TextStyleColor): string {
    return `sir-text-style-color-${color}`;
}
